/**
 * Recipe Image Upload Utility
 * Handles two-step presigned URL upload flow for recipe images
 */

import { apiRequest } from './queryClient';
import type {
    RequestUploadUrlDTO,
    UploadUrlResponse,
    RecipeImageUploadResult
} from '../types/api-models';

/**
 * Step 1: Request upload URL from API
 * @param recipeId - The recipe ID (UUID string)
 * @param filename - Original filename
 * @param contentType - MIME type of the file
 * @returns Promise<UploadUrlResponse> - Upload URL and image key
 */
export async function requestUploadUrl(
    recipeId: string,
    filename: string,
    contentType: string
): Promise<UploadUrlResponse> {
    // Extract extension from content type or filename
    const getExtension = (filename: string, contentType: string): string => {
        // Try to get extension from filename first
        const fileExt = filename.split('.').pop()?.toLowerCase();
        if (fileExt && ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(fileExt)) {
            return fileExt === 'jpeg' ? 'jpg' : fileExt;
        }

        // Fallback to content type
        switch (contentType) {
            case 'image/png': return 'png';
            case 'image/webp': return 'webp';
            case 'image/gif': return 'gif';
            case 'image/jpeg':
            case 'image/jpg':
            default: return 'jpg';
        }
    };

    const extension = getExtension(filename, contentType);

    const requestData: RequestUploadUrlDTO = {
        filename,
        contentType,
        extension
    };

    const response = await apiRequest('POST', `/recipes/${recipeId}/image/upload-url`, requestData);

    if (!response.ok) {
        throw new Error('Could not prepare upload');
    }

    return response.json();
}

/**
 * Step 2: Upload file directly to S3 using presigned URL
 * @param uploadUrl - Presigned URL from step 1
 * @param file - File to upload
 * @param contentType - MIME type of the file
 * @returns Promise<void>
 */
export async function uploadToS3(
    uploadUrl: string,
    file: File,
    contentType: string
): Promise<void> {
    // Use the same content-type logic as mediaRequest
    const headers: Record<string, string> = {};

    // Set Content-Type to file's type, with fallback to provided contentType
    headers["Content-Type"] = file.type || contentType || "application/octet-stream";

    // Cache-Control must match what the backend signed into the presigned URL
    headers['Cache-Control'] = 'public, max-age=31536000, immutable';

    const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers,
        body: file
    });

    if (!response.ok) {
        throw new Error('Upload failed');
    }
}

/**
 * Step 3: Confirm upload completion
 * @param recipeId - The recipe ID (UUID string)
 * @param imageKey - Image key from step 1
 * @returns Promise<RecipeImageUploadResult> - Result with image key
 */
export async function confirmUpload(
    recipeId: string,
    imageKey: string
): Promise<RecipeImageUploadResult> {
    const response = await apiRequest('POST', `/recipes/${recipeId}/images/confirm`, {
        imageKey
    });

    if (!response.ok) {
        throw new Error('Confirmation failed');
    }

    return response.json();
}

/**
 * Complete recipe image upload flow
 * @param recipeId - The recipe ID (UUID string)
 * @param file - File to upload
 * @returns Promise<RecipeImageUploadResult> - Result with image key
 */
export async function uploadRecipeImage(
    recipeId: string,
    file: File
): Promise<RecipeImageUploadResult> {
    const filename = file.name;
    const contentType = file.type;

    let uploadUrl: string;
    let imageKey: string;
    try {
        ({ uploadUrl, imageKey } = await requestUploadUrl(recipeId, filename, contentType));
    } catch (e) {
        console.error('Image upload - step 1 failed:', e);
        throw new Error('Could not prepare upload', { cause: e });
    }

    try {
        await uploadToS3(uploadUrl, file, contentType);
    } catch (e) {
        console.error('Image upload - step 2 failed:', e);
        throw new Error('Upload failed', { cause: e });
    }

    try {
        return await confirmUpload(recipeId, imageKey);
    } catch (e) {
        console.error('Image upload - step 3 failed:', e);
        throw new Error('Confirmation failed', { cause: e });
    }
}
